/**
 * KB Graph — the knowledge base drawn as nodes and edges.
 *
 * Answers "what shape is this KB": which clusters exist, what bridges them, and
 * what is connected to nothing. Isolated nodes are the finding, not a rendering
 * failure — see server/insights/graph/repository.ts for why the edges are
 * deliberately sparse.
 *
 * This file owns the route, the chrome and the data; kb_graph_canvas.ts owns
 * the pixels and kb_graph_layout.ts owns the maths.
 */

import { esc } from '../../shared/rendering.js'
import { route } from '../../router.js'
import { fetchKBGraph, type KBGraphData } from '../../api/insights.js'
import { fetchKBEntryDetail } from '../../api/kb.js'
import { kbTypeColor } from './kb_types.js'
import { layoutGraph, type LayoutNode } from './kb_graph_layout.js'
import { mountGraphCanvas, type GraphCanvas } from './kb_graph_canvas.js'
import { renderGraphPanel, type PanelNeighbour } from './kb_graph_panel.js'
import type { KBEntryDetail } from '../../types/api-secondary.js'

/** The layout runs in its own coordinate space and is then fitted to the canvas. */
const LAYOUT_WIDTH = 1800
const LAYOUT_HEIGHT = 1300

let _data: KBGraphData | null = null
let _positions: LayoutNode[] = []
let _error = false
let _canvas: GraphCanvas | null = null

// Selection state. The canvas keeps its own copy for painting; this is the copy
// the panel renders from, and the two are kept in step through select().
let _selected: string | null = null
let _entry: KBEntryDetail | null = null
let _entryLoading = false
let _entryError = false

function renderLegend(data: KBGraphData): string {
  const counts = new Map<string, number>()
  for (const n of data.nodes) counts.set(n.type, (counts.get(n.type) ?? 0) + 1)
  const items = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(
      ([type, count]) =>
        `<span class="kg-key"><i style="background:var(--${kbTypeColor(type)})"></i>${esc(type)} <b>${String(count)}</b></span>`,
    )
    .join('')
  return `<div class="kg-legend" role="group" aria-label="Entry types">${items}</div>`
}

function renderShell(data: KBGraphData | null): string {
  const header = `<header class="page-header">
    <div>
      <h1 class="page-title">Knowledge Graph</h1>
      <p class="page-subtitle">Entries linked by shared rare tags and hand-drawn relations</p>
    </div>
  </header>`

  if (_error) return `${header}<div class="empty">Could not load the graph.</div>`
  if (!data) return `${header}<div class="loading"><div class="spinner"></div></div>`
  if (data.nodes.length === 0) {
    return `${header}<div class="empty">No entries in this project yet.</div>`
  }

  const linked = new Set<string>()
  for (const e of data.edges) {
    linked.add(e.from)
    linked.add(e.to)
  }
  const isolated = data.nodes.length - linked.size
  const drawn = data.edges.filter((e) => e.kind === 'relation').length

  // The isolated count is stated plainly rather than hidden: in a view about
  // shape, "412 entries connect to nothing" is the most actionable number here.
  const stats = `<div class="kg-stats" role="status">
    <span><b>${String(data.nodes.length)}</b> entries</span>
    <span><b>${String(data.edges.length)}</b> links</span>
    <span><b>${String(isolated)}</b> isolated</span>
    <span><b>${String(drawn)}</b> hand-drawn</span>
  </div>`

  return `${header}${stats}${renderLegend(data)}
    <div class="kg-stage" id="kg-stage">
      <div class="kg-wrap">
        <canvas id="kg-canvas" class="kg-canvas" role="img"
          aria-label="Knowledge base graph: ${String(data.nodes.length)} entries, ${String(data.edges.length)} links, ${String(isolated)} isolated"></canvas>
        <div id="kg-tip" class="kg-tip" hidden></div>
      </div>
      ${renderGraphPanel({
        selectedId: _selected,
        entry: _entry,
        loading: _entryLoading,
        error: _entryError,
        neighbours: neighboursOf(data, _selected),
      })}
    </div>
    <p class="kg-hint">Scroll to zoom · drag to pan · click a node to select it and see the
      entry. The faint ring around the graph is the ${String(isolated)} entries with no
      links — they orbit rather than sit anywhere in particular, because an entry with no
      edges has no position that would mean anything.</p>`
}

/**
 * The selected node's neighbours, straight from the graph data.
 *
 * Deliberately NOT from the entry's own `related_kb`: that field is hand-drawn
 * relations only, so it would disagree with the lines actually on screen and
 * report nothing for the tag-derived edges that make up the whole graph today.
 */
function neighboursOf(data: KBGraphData, id: string | null): PanelNeighbour[] {
  if (!id) return []
  const byId = new Map(data.nodes.map((n) => [n.id, n]))
  const out: PanelNeighbour[] = []
  for (const e of data.edges) {
    if (e.from !== id && e.to !== id) continue
    const other = byId.get(e.from === id ? e.to : e.from)
    if (!other) continue
    out.push({ id: other.id, title: other.title, type: other.type, kind: e.kind, via: e.via })
  }
  // Hand-drawn first, then strongest tag overlap: the same precedence the
  // server ranks edges by, so the list agrees with what survived the cap.
  return out.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'relation' ? -1 : 1
    return b.via.length - a.via.length || a.title.localeCompare(b.title)
  })
}

function repaint(): void {
  const el = document.getElementById('kg-content')
  if (el) el.innerHTML = renderShell(_data)
}

function mount(): void {
  const canvas = document.getElementById('kg-canvas')
  if (!(canvas instanceof HTMLCanvasElement) || !_data) return
  _canvas = mountGraphCanvas(canvas, document.getElementById('kg-tip'), _data, _positions, (id) => {
    void selectNode(id)
  })
}

/**
 * Select a node: highlight it in the canvas and show its entry beside the graph.
 *
 * Repaints the panel only — NOT the whole view. Rebuilding the shell would
 * replace the canvas element, throwing away the mounted listeners and the
 * camera, so a click would reset the pan and zoom the user had set up.
 */
async function selectNode(id: string | null): Promise<void> {
  _selected = id
  _entry = null
  _entryError = false
  _entryLoading = id !== null
  _canvas?.select(id)
  repaintPanel()
  if (!id) return

  try {
    const entry = await fetchKBEntryDetail(id)
    // The selection can change while this is in flight — a second click, or a
    // click on empty space. Late replies for a node that is no longer selected
    // must not overwrite the panel.
    if (_selected !== id) return
    _entry = entry
  } catch {
    if (_selected !== id) return
    _entryError = true
  }
  _entryLoading = false
  repaintPanel()
}

function repaintPanel(): void {
  const stage = document.getElementById('kg-stage')
  if (!stage || !_data) return
  stage.querySelector('.kg-panel')?.remove()
  const html = renderGraphPanel({
    selectedId: _selected,
    entry: _entry,
    loading: _entryLoading,
    error: _entryError,
    neighbours: neighboursOf(_data, _selected),
  })
  if (html) stage.insertAdjacentHTML('beforeend', html)
}

/** Close the panel and clear the highlight. Exported for the action registry. */
export function closeGraphPanel(): void {
  void selectNode(null)
}

/** Jump the selection to a neighbour from inside the panel. */
export function selectGraphNode(id: string): void {
  void selectNode(id)
}

async function load(): Promise<void> {
  try {
    _data = await fetchKBGraph()
    // Laid out once, synchronously, while the spinner is still up. Animating
    // instead would mean the graph moves while you are trying to point at it.
    _positions = layoutGraph(_data, { width: LAYOUT_WIDTH, height: LAYOUT_HEIGHT })
  } catch {
    _data = null
    _positions = []
    _error = true
  }
  repaint()
  if (_data) mount()
}

route('/graph', () => {
  const app = document.getElementById('app')
  if (!app) return
  // Tear the previous canvas down first: it holds window-level listeners that
  // would otherwise keep firing against a detached element.
  _canvas?.destroy()
  _canvas = null
  _data = null
  _positions = []
  _error = false
  _selected = null
  _entry = null
  _entryLoading = false
  _entryError = false

  document.title = 'Knowledge Graph — Grizzly'
  app.innerHTML = '<div id="kg-content"></div>'
  repaint()
  void load()
})

// Types live in action-registry.ts's Window interface augmentation.
window.closeGraphPanel = closeGraphPanel
window.selectGraphNode = selectGraphNode

// Escape closes the panel — the keyboard equivalent of clicking empty space.
document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Escape' && _selected !== null && location.hash.startsWith('#/graph')) {
    closeGraphPanel()
  }
})
